import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Skeleton, Stack, Select, MenuItem
  , TextField, FormControl, InputLabel, FormHelperText, Checkbox, FormControlLabel
} from '@mui/material';
import adCenterApi from '../adCenterApi';


import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { showToast } from '../CoreDataService';
import { useNavigate } from 'react-router-dom';


export default function PublishCampaign() {
    const navigate = useNavigate();
  
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [spendCaps, setSpendCaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAudience, setSelectedAudience] = useState({});
  const [selectedSpendCap, setSelectedSpendCap] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch published campaigns
        const campaignRes = await adCenterApi.post('/getPublishedCampaignByBroker', {
          adminUser: localStorage.getItem('uuid'),
          brokerId: localStorage.getItem('brokerId'),
        });
        setCampaigns(campaignRes.data?.publishCampaignDetails || []);

        // Fetch audiences
        const audienceRes = await adCenterApi.post('/getAudienceByBroker', {
          adminUser: localStorage.getItem('uuid'),
          brokerId: localStorage.getItem('brokerId'),
        });
        setAudiences(audienceRes.data?.audienceDetails || []);

        // Fetch spend caps
        const spendCapRes = await adCenterApi.post('/getSpendCapByBroker', { brokerId: localStorage.getItem('brokerId') });
        setSpendCaps(spendCapRes.data?.spendCapDetails || []);
      } catch (err) {
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Track selected audience and spend cap per campaign
  const handleAudienceChange = (campaignId, value) => {
    setSelectedAudience((prev) => ({ ...prev, [campaignId]: value }));
  };
  const handleSpendCapChange = (campaignId, value) => {
    setSelectedSpendCap((prev) => ({ ...prev, [campaignId]: value }));
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
              Publish Campaign
            </Typography>
            <Typography variant="body1" color="text.secondary" mt={0.5}>
              <em>Launch your campaign to reach your target audience.</em>
            </Typography>
          </Box>
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ height: 64 }}>
  <TableCell sx={{ fontWeight: 'bold', color: '#fff', height: 64 }}>Campaign Name</TableCell>
  <TableCell sx={{ fontWeight: 'bold', color: '#fff', height: 64 }}>Audience</TableCell>
  <TableCell sx={{ fontWeight: 'bold', color: '#fff', height: 64 }}>Spend Caps</TableCell>
  <TableCell sx={{ fontWeight: 'bold', color: '#fff', height: 64 }}>Set an End Date</TableCell>
  <TableCell sx={{ fontWeight: 'bold', color: '#fff', height: 64 }}>End Date</TableCell>
  <TableCell sx={{ fontWeight: 'bold', color: '#fff', height: 64 }}>Action</TableCell>
</TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Stack spacing={2}>
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ color: 'red' }}>{error}</TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No campaigns found.</TableCell>
                </TableRow>
              ) : (
                campaigns.map((row, idx) => {
                  const campaignId = row.publishId || idx;

                  // Default to 30 days from now
                  const defaultEndDate = new Date();
                  defaultEndDate.setDate(defaultEndDate.getDate() + 30);
                  const formattedDefaultDate = defaultEndDate.toISOString().split('T')[0];

                  // If API endDate is present, use it and check the checkbox
                  const apiEndDate = row.endDate ? dayjs(row.endDate, ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']) : null;
                  const isChecked = selectedAudience[campaignId]?.setEndDate
                    ?? (row.endDate ? true : false);
                  const selectedDate = selectedAudience[campaignId]?.endDate
                    || (apiEndDate ? apiEndDate.format('YYYY-MM-DD') : formattedDefaultDate);

                  return (
                    <TableRow
                      key={campaignId} hover sx={{ height: 64 }}
                      
                      // sx={{
                      //   transition: 'background 0.2s',
                      //   '&:hover': { backgroundColor: 'grey.100' },
                      // }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{row.campaignName || '-'}</TableCell>

                      <TableCell>
                        <Select
                          value={
                            selectedAudience[campaignId]?.audienceId
                              ?? row.audienceId
                              ?? ''
                          }
                          onChange={(e) => handleAudienceChange(campaignId, {
                            ...selectedAudience[campaignId],
                            audienceId: e.target.value,
                          })}
                          displayEmpty
                          size="small"
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value=""><em>Select Audience</em></MenuItem>
                          {audiences.map((aud) => (
                            <MenuItem key={aud.audienceId} value={aud.audienceId}>
                              {aud.audienceName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell>
                        <Select
                          value={
                            selectedSpendCap[campaignId]
                              ?? row.spendCapsId
                              ?? ''
                          }
                          onChange={(e) => handleSpendCapChange(campaignId, e.target.value)}
                          displayEmpty
                          size="small"
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value=""><em>Select Spend Cap</em></MenuItem>
                          {spendCaps.map((cap) => (
                            <MenuItem key={cap.spendCapsId} value={cap.spendCapsId}>
                              {cap.spendCapsName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) =>
                            handleAudienceChange(campaignId, {
                              ...selectedAudience[campaignId],
                              setEndDate: e.target.checked,
                            })
                          }
                        />
                      </TableCell>

                        <TableCell sx={{ height: 64, verticalAlign: 'middle' }}>
    {isChecked && (
      <Box sx={{ maxWidth: 140, minWidth: 120 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="YYYY-MM-DD"
            value={dayjs(selectedDate)}
            onChange={(newValue) =>
              handleAudienceChange(campaignId, {
                ...selectedAudience[campaignId],
                endDate: newValue.format('YYYY-MM-DD'),
                setEndDate: true,
              })
            }
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: false,
                sx: { fontSize: 13, minWidth: 120, maxWidth: 140 },
                InputProps: { sx: { fontSize: 13 } }
              },
            }}
          />
        </LocalizationProvider>
      </Box>
    )}
  </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                          {/* Initial State: Publish */}
                          {row.publishStatus === 0 && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: 2 }}
                              disabled={selectedAudience[campaignId]?.inReview}
                              onClick={() => {
                                // Set inReview state locally for instant feedback
                                handleAudienceChange(campaignId, {
                                  ...selectedAudience[campaignId],
                                  inReview: true,
                                });

                                // Prepare payload
                                const payload = {
                                  adminUser: localStorage.getItem('uuid'),
                                  campaignId: row.campaignId || campaignId,
                                  spendCapsId: selectedSpendCap[campaignId],
                                  audienceId: selectedAudience[campaignId]?.audienceId,
                                  remarks: '', // You can replace with a dynamic value if needed
                                  endDateFlag: selectedAudience[campaignId]?.setEndDate ? 1 : 0,
                                  endDate: selectedAudience[campaignId]?.setEndDate
                                    ? dayjs(selectedAudience[campaignId]?.endDate).format('DD/MM/YYYY')
                                    : '',
                                };

                                console.log('Publishing campaign with payload:', payload);

                                adCenterApi.post('/publishCampaign', payload)
                                  .then((res) => {
                                    if (res.data.statusCode === 0) {
                                      // Optionally, refresh campaigns or update UI
                                      handleAudienceChange(campaignId, {
                                        ...selectedAudience[campaignId],
                                        inReview: true,
                                      });
                                    } else {
                                      // Handle error
                                      handleAudienceChange(campaignId, {
                                        ...selectedAudience[campaignId],
                                        inReview: false,
                                      });
                                      showToast('error',res.data.message || 'Failed to publish campaign');
                                    }
                                  })
                                  .catch(() => {
                                    handleAudienceChange(campaignId, {
                                      ...selectedAudience[campaignId],
                                      inReview: false,
                                    });
                                    showToast('error','Failed to publish campaign');
                                  });
                              }}
                            >
                              {selectedAudience[campaignId]?.inReview ? 'In Review' : 'Publish'}
                            </Button>
                          )}

                          {/* In Review State */}
                          {row.publishStatus === 1 && (
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: 2 }}
                              disabled
                            >
                              In Review
                            </Button>
                          )}

                          {/* Published State */}
                          {row.publishStatus === 2 && (
                            <>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                disabled
                              >
                                Published
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                onClick={async () => {
                                  const payload = {
                                    adminUser: localStorage.getItem('uuid'),
                                    publishId: row.publishId || row.campaignId,
                                    publishStatus: 4, // 0 for Unpublish
                                    spendCapsId: selectedSpendCap[campaignId] ?? row.spendCapsId,
                                    audienceId: selectedAudience[campaignId]?.audienceId ?? row.audienceId,
                                    remarks: row.remarks || 'Unpublished by broker',
                                    endDateFlag: selectedAudience[campaignId]?.setEndDate ? 1 : (row.endDate ? 1 : 0),
                                    endDate: selectedAudience[campaignId]?.setEndDate
                                      ? dayjs(selectedAudience[campaignId]?.endDate).format('DD/MM/YYYY')
                                      : (row.endDate ? row.endDate : ''),
                                  };

                                  try {
                                    const res = await adCenterApi.post('/updatePublishCampaignStatus', payload);
                                    if (res.data.statusCode === 0) {
                                      showToast('success','Campaign unpublished successfully');
                                      
                                      setTimeout(() => {
                                        window.location.reload(); // Reload to reflect changes
                                        
                                      }, 1500);
                                      // Optionally refresh campaigns or update UI
                                      // You may want to refetch campaigns here
                                    } else {
                                      showToast('error',res.data.message || 'Failed to unpublish campaign');
                                    }
                                  } catch (err) {
                                    showToast('error','Failed to unpublish campaign');
                                  }
                                }}
                              >
                                Unpublish
                              </Button>
                            </>
                          )}

                          {/* Rejected State */}
                          {row.publishStatus === 3 && (
                            <>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                disabled
                              >
                                Rejected
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                // onClick={() => {
                                //   // Redirect to edit/view page
                                //   window.location.href = `/edit-campaign`;
                                // }}
                                onClick={() => navigate('/get-campaign')}
                              >
                                Edit
                              </Button>

                              <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: 2 }}
                              disabled={selectedAudience[campaignId]?.inReview}
                              onClick={() => {
                                // Set inReview state locally for instant feedback
                                handleAudienceChange(campaignId, {
                                  ...selectedAudience[campaignId],
                                  inReview: true,
                                });

                                // Prepare payload
                                const payload = {
                                    adminUser: localStorage.getItem('uuid'),
                                    publishId: row.publishId || row.campaignId,
                                    publishStatus: 1, 
                                    spendCapsId: selectedSpendCap[campaignId] ?? row.spendCapsId,
                                    audienceId: selectedAudience[campaignId]?.audienceId ?? row.audienceId,
                                    remarks: '',
                                    endDateFlag: selectedAudience[campaignId]?.setEndDate ? 1 : (row.endDate ? 1 : 0),
                                    endDate: selectedAudience[campaignId]?.setEndDate
                                      ? dayjs(selectedAudience[campaignId]?.endDate).format('DD/MM/YYYY')
                                      : (row.endDate ? row.endDate : ''),
                                  };

                                console.log('Publishing campaign with payload:', payload);

                                adCenterApi.post('/updatePublishCampaignStatus', payload)
                                  .then((res) => {
                                    if (res.data.statusCode === 0) {
                                      // Optionally, refresh campaigns or update UI
                                      handleAudienceChange(campaignId, {
                                        ...selectedAudience[campaignId],
                                        inReview: true,
                                      });
                                      showToast('success','Published campaign successfully');
                                      setTimeout(() => {
                                      window.location.reload(); // Reload to reflect changes
                                        
                                      }, 1500);

                                    } else {
                                      // Handle error
                                      handleAudienceChange(campaignId, {
                                        ...selectedAudience[campaignId],
                                        inReview: false,
                                      });
                                      showToast('error',res.data.message || 'Failed to publish campaign');
                                    }
                                  })
                                  .catch(() => {
                                    handleAudienceChange(campaignId, {
                                      ...selectedAudience[campaignId],
                                      inReview: false,
                                    });
                                    showToast('error','Failed to publish campaign');
                                  });
                              }}
                            >
                              RePublish
                            </Button>
                            </>
                          )}

                          {/* Unpublished State */}
                          {row.publishStatus === 4 && (
                            <Button
                              variant="contained"
                              color="info"
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: 2 }}
                              disabled
                            >
                              Unpublished
                            </Button>
                          )}

                          {/* Paused State */}
                          {row.publishStatus === 5 && (
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              sx={{ fontWeight: 'bold', borderRadius: 2 }}
                              disabled
                            >
                              Paused
                            </Button>
                          )}
                        </Box>
                        {/* Rejected comment */}
                        {row.publishStatus === 3 && row.rejectionComment && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {row.rejectionComment}
                          </Typography>
                        )}
                        {/* Remarks from API */}
{row.remarks && (
  <Typography
    variant="body2"
    sx={{
      mt: 1,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxWidth: 240,
    }}
    color="text.secondary"
  >
    Remarks:{row.remarks}
  </Typography>
)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>

          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
}
